import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../lib/api';
import type { CustomerListItem, Message, ConversationResponse } from '../types';
import { Search, MessageCircle, Loader2 } from 'lucide-react';
import MessageBubble from '../components/MessageBubble';
import DateDivider from '../components/DateDivider';
import { formatMessageDate, isSameDay } from '../utils/dateHelpers';

export default function ConversationsView() {
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [lastReadMessageId, setLastReadMessageId] = useState<number | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerMobileRef = useRef<HTMLDivElement>(null);
  const firstUnreadRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  const LIMIT = 50;

  // Fetch customers list
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await apiClient.get<CustomerListItem[]>('/api/conversations/customers');
      setCustomers(data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'טעינת השיחות נכשלה');
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark conversation as read
  const markAsRead = useCallback(async (phoneNumber: string, lastMessageId: number) => {
    try {
      await apiClient.post(`/api/conversations/${encodeURIComponent(phoneNumber)}/mark_read`, {
        last_message_id: lastMessageId,
      });
      // Refresh customers list to update unread counts
      fetchCustomers();
    } catch (err) {
      console.error('Error marking conversation as read:', err);
    }
  }, [fetchCustomers]);

  // Fetch messages for a customer
  const fetchMessages = useCallback(async (phoneNumber: string, messageOffset: number = 0, append: boolean = false) => {
    // Prevent duplicate calls
    if (append && isLoadingMoreRef.current) {
      return;
    }
    
    try {
      if (messageOffset === 0) {
        setLoadingMessages(true);
        setInitialScrollDone(false);
      } else {
        setLoadingMore(true);
        isLoadingMoreRef.current = true;
      }
      setError(null);
      
      // Get scroll position before loading more
      const desktopContainer = messagesContainerRef.current;
      const mobileContainer = messagesContainerMobileRef.current;
      const prevScrollHeight = desktopContainer?.scrollHeight || mobileContainer?.scrollHeight || 0;
      
      const { data } = await apiClient.get<ConversationResponse>(
        `/api/conversations/${encodeURIComponent(phoneNumber)}`,
        {
          params: {
            limit: LIMIT,
            offset: messageOffset,
          },
        }
      );

      setLastReadMessageId(data.last_read_message_id ?? null);
      setHasMore(data.has_more);

      if (append) {
        // Prepend older messages
        setMessages((prev) => [...(data.messages || []), ...prev]);
        setOffset(messageOffset + (data.messages?.length || 0));
        
        // Restore scroll position
        requestAnimationFrame(() => {
          if (desktopContainer) {
            desktopContainer.scrollTop = desktopContainer.scrollHeight - prevScrollHeight;
          }
          if (mobileContainer) {
            mobileContainer.scrollTop = mobileContainer.scrollHeight - prevScrollHeight;
          }
        });
      } else {
        // Replace messages (new conversation)
        setMessages(data.messages || []);
        setOffset(data.messages?.length || 0);
        
        // Smart scroll: to first unread or to bottom
        requestAnimationFrame(() => {
          const container = desktopContainer || mobileContainer;
          if (!container) return;

          // If there are unread messages, scroll to first unread
          if (data.last_read_message_id && firstUnreadRef.current) {
            firstUnreadRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            // Otherwise scroll to bottom
            container.scrollTop = container.scrollHeight;
          }
          
          setInitialScrollDone(true);
          
          // Mark all messages as read after viewing
          if (data.messages && data.messages.length > 0) {
            const latestMessageId = data.messages[data.messages.length - 1].id;
            markAsRead(phoneNumber, latestMessageId);
          }
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'טעינת ההודעות נכשלה');
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [markAsRead]);

  // Load more messages
  const loadMoreMessages = useCallback(() => {
    if (selectedCustomer && hasMore && !loadingMore && !isLoadingMoreRef.current) {
      fetchMessages(selectedCustomer, offset, true);
    }
  }, [selectedCustomer, hasMore, loadingMore, offset, fetchMessages]);

  // Handle scroll event to detect when user scrolls to top
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    // Load more when scrolled near top (within 100px)
    if (container.scrollTop < 100 && hasMore && !loadingMore && !loadingMessages && initialScrollDone && !isLoadingMoreRef.current) {
      loadMoreMessages();
    }
  }, [hasMore, loadingMore, loadingMessages, initialScrollDone, loadMoreMessages]);

  // Initial load
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Load messages when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      setOffset(0);
      setHasMore(false);
      setInitialScrollDone(false);
      fetchMessages(selectedCustomer, 0, false);
    } else {
      setMessages([]);
      setInitialScrollDone(false);
    }
  }, [selectedCustomer, fetchMessages]);

  // Format timestamp for customer list
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'עכשיו';
    if (diffMins < 60) return `לפני ${diffMins} דק'`;
    if (diffHours < 24) return `לפני ${diffHours} שעות`;
    if (diffDays < 7) return `לפני ${diffDays} ימים`;
    
    return date.toLocaleDateString('he-IL', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Group messages by date with dividers
  const renderMessagesWithDividers = () => {
    const elements: React.ReactElement[] = [];
    let previousDate: string | null = null;
    let firstUnreadFound = false;

    messages.forEach((message, index) => {
      const currentDate = message.created_at;
      
      // Add date divider if date changed
      if (!previousDate || !isSameDay(previousDate, currentDate)) {
        elements.push(
          <DateDivider key={`date-${index}`} date={formatMessageDate(currentDate)} />
        );
      }
      
      // Check if this is the first unread message
      const isFirstUnread = !firstUnreadFound && lastReadMessageId !== null && message.id > lastReadMessageId;
      if (isFirstUnread) {
        firstUnreadFound = true;
      }
      
      elements.push(
        <div 
          key={message.id} 
          ref={isFirstUnread ? firstUnreadRef : null}
        >
          <MessageBubble
            role={message.role as 'user' | 'assistant'}
            content={message.content}
            timestamp={message.created_at}
            isFirstUnread={isFirstUnread}
          />
        </div>
      );
      
      previousDate = currentDate;
    });

    return elements;
  };

  // Filter customers by search query
  const filteredCustomers = customers.filter((customer) =>
    customer.phone_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.last_message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Customers List */}
        <div className="w-full md:w-[35%] border-l border-gray-200 flex flex-col bg-gray-50">
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="חפש שיחות..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Customers List */}
          <div className="flex-1 overflow-y-auto">
            {error && (
              <div className="p-4 text-sm text-red-600 bg-red-50 border-b border-red-200">
                {typeof error === 'string' ? error : String(error)}
              </div>
            )}
            
            {filteredCustomers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                <MessageCircle className="w-12 h-12 mb-4 text-gray-300" />
                <p className="text-sm text-center">
                  {searchQuery ? 'לא נמצאו שיחות התואמות לחיפוש' : 'אין שיחות עדיין'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <button
                    key={customer.phone_number}
                    onClick={() => setSelectedCustomer(customer.phone_number)}
                    className={`w-full p-4 text-right hover:bg-gray-100 transition-colors ${
                      selectedCustomer === customer.phone_number ? 'bg-gray-100' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className={`text-sm truncate ${(customer.unread_count || 0) > 0 ? 'font-bold' : 'font-medium'}`}>
                            {customer.phone_number}
                          </h3>
                          <span className="text-xs text-gray-500 mr-2">
                            {formatTimestamp(customer.last_message_timestamp)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className={`text-xs text-gray-600 truncate flex-1 ${(customer.unread_count || 0) > 0 ? 'font-semibold' : ''}`}>
                            {customer.last_message}
                          </p>
                          {(customer.unread_count || 0) > 0 && (
                            <span className="flex-shrink-0 bg-green-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                              {customer.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Chat Area */}
        <div className="hidden md:flex flex-1 flex-col">
          {selectedCustomer ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <h2 className="font-semibold text-gray-900">{selectedCustomer}</h2>
                <p className="text-xs text-gray-500">לקוח</p>
              </div>

              {/* Messages Area with WhatsApp Background */}
              <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='260' viewBox='0 0 260 260'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23e5ddd5' fill-opacity='0.08'%3E%3Cpath d='M24.37 16c.2.65.39 1.32.54 2H21.17l1.17 2.34.45.9-.24.11V28a5 5 0 0 1-2.23 8.94l-.02.06a8 8 0 0 1-7.75 6h-20a8 8 0 0 1-7.74-6l-.02-.06a5 5 0 0 1-2.24-8.94v-6.76l-.79-1.58-.44-.9.9-.44.63-.32H-20a23.01 23.01 0 0 1 44.37-2zm-36.82 2a1 1 0 0 0-.44.1l-3.1 1.56.89 1.79 1.31-.66a3 3 0 0 1 2.69 0l2.2 1.1a1 1 0 0 0 .9 0l2.21-1.1a3 3 0 0 1 2.69 0l2.2 1.1a1 1 0 0 0 .9 0l2.21-1.1a3 3 0 0 1 2.69 0l2.2 1.1a1 1 0 0 0 .86.02l2.88-1.27a3 3 0 0 1 2.43 0l2.88 1.27a1 1 0 0 0 .85-.02l3.1-1.55-.89-1.79-1.42.71a3 3 0 0 1-2.56.06l-2.77-1.23a1 1 0 0 0-.4-.09h-.01a1 1 0 0 0-.4.09l-2.78 1.23a3 3 0 0 1-2.56-.06l-2.3-1.15a1 1 0 0 0-.45-.11h-.01a1 1 0 0 0-.44.1L.9 19.22a3 3 0 0 1-2.69 0l-2.2-1.1a1 1 0 0 0-.45-.11h-.01a1 1 0 0 0-.44.1l-2.21 1.11a3 3 0 0 1-2.69 0l-2.2-1.1a1 1 0 0 0-.45-.11h-.01zm0-2h-4.9a21.01 21.01 0 0 1 39.61 0h-2.09l-.06-.13-.26.13h-32.31zm30.35 7.68l1.36-.68h1.3v2h-36v-1.15l.34-.17 1.36-.68h2.59l1.36.68a3 3 0 0 0 2.69 0l1.36-.68h2.59l1.36.68a3 3 0 0 0 2.69 0L2.26 23h2.59l1.36.68a3 3 0 0 0 2.56.06l1.67-.74h3.23l1.67.74a3 3 0 0 0 2.56-.06zM-13.82 27l16.37 4.91L18.93 27h-32.75zm-.63 2h.34l16.66 5 16.67-5h.33a3 3 0 1 1 0 6h-34a3 3 0 1 1 0-6zm1.35 8a6 6 0 0 0 5.65 4h20a6 6 0 0 0 5.66-4H-13.1z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  backgroundColor: '#e5ddd5',
                }}
              >
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <>
                    {loadingMore && (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      </div>
                    )}
                    {renderMessagesWithDividers()}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">בחר שיחה כדי להתחיל</p>
              </div>
            </div>
          )}
        </div>

        {/* Mobile View - Full Screen Chat */}
        {selectedCustomer && (
          <div className="fixed inset-0 z-50 bg-white md:hidden flex flex-col">
            {/* Mobile Header */}
            <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-3">
              <button
                onClick={() => setSelectedCustomer(null)}
                className="text-gray-600 hover:text-gray-900"
              >
                ←
              </button>
              <div>
                <h2 className="font-semibold text-gray-900">{selectedCustomer}</h2>
                <p className="text-xs text-gray-500">לקוח</p>
              </div>
            </div>

            {/* Mobile Messages with WhatsApp Background */}
            <div
              ref={messagesContainerMobileRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='260' viewBox='0 0 260 260'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23e5ddd5' fill-opacity='0.08'%3E%3Cpath d='M24.37 16c.2.65.39 1.32.54 2H21.17l1.17 2.34.45.9-.24.11V28a5 5 0 0 1-2.23 8.94l-.02.06a8 8 0 0 1-7.75 6h-20a8 8 0 0 1-7.74-6l-.02-.06a5 5 0 0 1-2.24-8.94v-6.76l-.79-1.58-.44-.9.9-.44.63-.32H-20a23.01 23.01 0 0 1 44.37-2zm-36.82 2a1 1 0 0 0-.44.1l-3.1 1.56.89 1.79 1.31-.66a3 3 0 0 1 2.69 0l2.2 1.1a1 1 0 0 0 .9 0l2.21-1.1a3 3 0 0 1 2.69 0l2.2 1.1a1 1 0 0 0 .9 0l2.21-1.1a3 3 0 0 1 2.69 0l2.2 1.1a1 1 0 0 0 .86.02l2.88-1.27a3 3 0 0 1 2.43 0l2.88 1.27a1 1 0 0 0 .85-.02l3.1-1.55-.89-1.79-1.42.71a3 3 0 0 1-2.56.06l-2.77-1.23a1 1 0 0 0-.4-.09h-.01a1 1 0 0 0-.4.09l-2.78 1.23a3 3 0 0 1-2.56-.06l-2.3-1.15a1 1 0 0 0-.45-.11h-.01a1 1 0 0 0-.44.10L.9 19.22a3 3 0 0 1-2.69 0l-2.2-1.1a1 1 0 0 0-.45-.11h-.01a1 1 0 0 0-.44.1l-2.21 1.11a3 3 0 0 1-2.69 0l-2.2-1.1a1 1 0 0 0-.45-.11h-.01zm0-2h-4.9a21.01 21.01 0 0 1 39.61 0h-2.09l-.06-.13-.26.13h-32.31zm30.35 7.68l1.36-.68h1.3v2h-36v-1.15l.34-.17 1.36-.68h2.59l1.36.68a3 3 0 0 0 2.69 0l1.36-.68h2.59l1.36.68a3 3 0 0 0 2.69 0L2.26 23h2.59l1.36.68a3 3 0 0 0 2.56.06l1.67-.74h3.23l1.67.74a3 3 0 0 0 2.56-.06zM-13.82 27l16.37 4.91L18.93 27h-32.75zm-.63 2h.34l16.66 5 16.67-5h.33a3 3 0 1 1 0 6h-34a3 3 0 1 1 0-6zm1.35 8a6 6 0 0 0 5.65 4h20a6 6 0 0 0 5.66-4H-13.1z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundColor: '#e5ddd5',
              }}
            >
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {loadingMore && (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  )}
                  {renderMessagesWithDividers()}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
